import { MouseEvent, ReactNode, useRef, useState } from "react"
import { createPortal } from "react-dom"

type Props = {
  text: string
  children: ReactNode
  /** Show delay in ms (default 500). */
  delay?: number
}

/**
 * Lightweight hover tooltip rendered through a portal with `position: fixed`, so it is never clipped
 * by an ancestor's `overflow` (unlike a daisyUI/CSS tooltip) and its show delay is configurable
 * (unlike the browser's native `title`). Positioned centered under the trigger.
 */
export const HoverTip = ({ text, children, delay = 500 }: Props) => {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onEnter = (e: MouseEvent<HTMLSpanElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = rect.left + rect.width / 2
    const y = rect.bottom
    timer.current = setTimeout(() => setPos({ x, y }), delay)
  }

  const onLeave = () => {
    if (timer.current) clearTimeout(timer.current)
    setPos(null)
  }

  return (
    <span onMouseEnter={onEnter} onMouseLeave={onLeave} className="inline-flex cursor-help align-middle">
      {children}
      {pos &&
        createPortal(
          <div
            style={{ position: "fixed", left: pos.x, top: pos.y + 8, transform: "translateX(-50%)", zIndex: 9999 }}
            className="pointer-events-none max-w-xs rounded-md bg-neutral-800 px-3 py-2 text-sm font-normal normal-case leading-snug text-white shadow-lg dark:bg-neutral-700"
          >
            {text}
          </div>,
          document.body
        )}
    </span>
  )
}
